import React, { useState } from 'react';
import styled from 'styled-components/macro';
import {
  useGetAccessQuery,
  Access,
  Role,
  useCountAccessQuery,
} from 'types/dt-graphql-apollo-v2';
import {
  Title,
  Body,
  Button,
  Dropdown,
  Input,
  Menu,
  Icon,
  Flex,
  Tooltip,
  Modal,
} from '@cognite/cogs.js';
import {
  InfoMessage,
  SimplePagination,
  Spinner,
  TableContainer,
  TitleBar,
} from 'components';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { sortStringOrUndefined } from 'utils/misc';
import { FeatureFlags, useFeatureFlag } from 'utils/feature-flags';
import { EmptyTemplate } from 'pages/EmptyTemplate/EmptyTemplate';
import { useAccessControl } from 'hooks/useAccessControl';
import pluralize from 'pluralize';
import { PAGINATION_PAGE_SIZE } from 'utils/config';
import { AddEditMemberDrawer } from './components/AddEditMemberDrawer';
import { DeleteMemberModal } from './components/DeleteMemberModal';
import { AccessControlMatrix } from './components/AccessControlMatrix';

export const AccessControl = () => {
  const enabledAccessControl = useFeatureFlag(
    FeatureFlags.enabledAccessControl
  );

  const [search, setSearch] = useState('');
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [deleteAccess, setDeleteAccess] = useState(false);
  const [showRolePermissions, setShowRolePermissions] = useState<Role | null>(
    null
  );
  const [selectedUserAccess, setSelectedUserAccess] = useState<Access | null>(
    null
  );

  const {
    data: accessControlData,
    error: accessControlError,
    loading: accessControlLoading,
    refetch: accessControlRefetch,
  } = useGetAccessQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      first: PAGINATION_PAGE_SIZE,
      cursor: null,
    },
  });

  const { data: countData, loading: countLoading } = useCountAccessQuery();

  const hasAccess = useAccessControl();
  const canCreateAccess = hasAccess(['Create_Access']);
  const canEditAccess = hasAccess(['Update_Access']);
  const canDeleteAccess = hasAccess(['Delete_Access']);

  const isLoading = accessControlLoading || countLoading;

  const handlePageChange = async (cursor: string | null) => {
    await accessControlRefetch({
      first: PAGINATION_PAGE_SIZE,
      cursor,
    });
  };

  const filterSearch = (searchValue: string, dataSource: Access[]) => {
    if (!searchValue || !dataSource) return dataSource;
    const searchTerm = searchValue.trim().toLowerCase();
    return dataSource?.filter((item) => {
      const searchTargets = [item.role, item.userId];
      return searchTargets.some(
        (searchTarget) =>
          searchTarget && searchTarget?.toLowerCase().includes(searchTerm)
      );
    });
  };

  const resetSelectedState = () => {
    setSelectedUserAccess(null);
    setDrawerOpened(false);
    setDeleteAccess(false);
  };

  const onSuccess = async () => {
    await accessControlRefetch();
    resetSelectedState();
  };

  const handleAccessEdit = (acccessToEdit: Access) => {
    setSelectedUserAccess(acccessToEdit);
    setDrawerOpened(true);
  };

  const handleAccessDelete = (acccessToDelete: Access) => {
    setSelectedUserAccess(acccessToDelete);
    setDeleteAccess(true);
  };

  const columns: ColumnsType<Access> = [
    {
      title: 'Email address',
      key: 'userId',
      sorter: (a, b) => sortStringOrUndefined(a?.userId, b?.userId),
      render: (record: Access) => (
        <Body level={1} strong>
          {record.userId}
        </Body>
      ),
    },
    {
      title: 'User role',
      key: 'role',
      sorter: (a, b) => sortStringOrUndefined(a?.role, b?.role),
      render: (record: Access) => (
        <Flex alignItems="center" gap={22}>
          <Body level={1} strong>
            {record.role}
          </Body>
          <Tooltip content="View information about the role">
            <Icon
              aria-label="Show information about the role."
              type="Help"
              style={{ verticalAlign: 'middle' }}
              onClick={() => setShowRolePermissions(record.role as Role)}
            />
          </Tooltip>
        </Flex>
      ),
    },
    {
      key: 'options',
      align: 'right',
      render: (record: Access) =>
        (canEditAccess || canDeleteAccess) && (
          <Dropdown
            content={
              <Menu>
                {canEditAccess && (
                  <Menu.Item
                    onClick={() => {
                      handleAccessEdit(record);
                    }}
                  >
                    <Icon
                      type="Edit"
                      style={{ verticalAlign: 'middle' }}
                      aria-label="Edit the access of a user."
                    />
                    <Body level={1}>Edit user access</Body>
                  </Menu.Item>
                )}

                {canDeleteAccess && (
                  <Menu.Item onClick={() => handleAccessDelete(record)}>
                    <StyledOptionsMenu direction="row">
                      <Icon
                        type="Delete"
                        style={{ verticalAlign: 'middle', marginTop: '0.2rem' }}
                        aria-label="Delete privileged access for a user."
                      />
                      <Flex direction="column" alignItems="start">
                        <Body level={1}>Delete privileged access</Body>
                        <Body level={3}>Removes the user from this list</Body>
                      </Flex>
                    </StyledOptionsMenu>
                  </Menu.Item>
                )}
              </Menu>
            }
          >
            <Button
              type="ghost"
              icon="EllipsisVertical"
              aria-label="Show access options for a user."
            />
          </Dropdown>
        ),
    },
  ];

  if (!enabledAccessControl) {
    return (
      <EmptyTemplate
        title="Page Not Found"
        description="Couldn't find the page you are looking for."
        graphic="EmptyStateSearch"
      />
    );
  }

  const renderPageContent = () => {
    if (accessControlError?.message) {
      return (
        <InfoMessage
          type="Error"
          title="Something went wrong while fetching the users."
        >
          {accessControlError.message}
        </InfoMessage>
      );
    }

    if (accessControlLoading) {
      return <Spinner />;
    }

    const accessControlUsers = accessControlData?.listAccess?.items as Access[];
    if (accessControlUsers.length === 0) {
      return (
        <InfoMessage type="Search" title="No users are defined yet.">
          You can configure Access Control and give specific privileges to new
          users.
        </InfoMessage>
      );
    }

    const filteredDataSource = filterSearch(search, accessControlUsers);

    const startCursor =
      accessControlData?.listAccess?.pageInfo.startCursor || null;
    const endCursor = accessControlData?.listAccess?.pageInfo.endCursor || null;
    const totalCount =
      countData?.aggregateAccess?.items[0]?.count?.externalId || 0;

    return (
      <>
        <Title level={5} style={{ margin: '1rem 2rem' }}>
          {`${filteredDataSource.length} privileged ${pluralize(
            'user',
            filteredDataSource.length
          )}
        `}
        </Title>
        <TableContainer>
          <Table<Access>
            style={{ margin: '0 2rem' }}
            rowKey="id"
            dataSource={filteredDataSource}
            rowClassName="row-clickable"
            columns={columns}
            loading={isLoading}
            pagination={false}
          />
        </TableContainer>

        <SimplePagination
          startCursor={startCursor}
          endCursor={endCursor}
          totalCount={totalCount}
          onChange={handlePageChange}
        />
      </>
    );
  };

  return (
    <>
      <AddEditMemberDrawer
        visible={drawerOpened}
        selectedUserAccess={selectedUserAccess}
        onCancel={resetSelectedState}
        onOk={onSuccess}
      />

      <DeleteMemberModal
        visible={deleteAccess}
        selectedUserAccess={selectedUserAccess}
        onCancel={resetSelectedState}
        onSuccess={onSuccess}
      />

      <TitleBar
        heading="Manage access within your team"
        subheading={
          <>
            <Body level={2}>
              Add users with Editor and Administrator access within this tenant.
            </Body>
            <Body level={2}>
              Users that are added in the identity provider service of your
              company automatically receive
              <Button
                aria-label="View permissions for the Reader role"
                size="small"
                type="ghost"
                onClick={() => setShowRolePermissions(Role.Viewer)}
              >
                <b>Reader</b>
              </Button>
              rights within the current tenant.
            </Body>
          </>
        }
      >
        <StyledSearchRow>
          <StyledInput
            placeholder="Search user"
            icon="Search"
            iconPlacement="left"
            value={search}
            disabled={!accessControlData?.listAccess?.items?.length}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          {canCreateAccess && (
            <Button
              type="primary"
              icon="AddLarge"
              onClick={() => setDrawerOpened(true)}
            >
              Add user
            </Button>
          )}
        </StyledSearchRow>
      </TitleBar>
      {renderPageContent()}

      <StyledModal
        hideFooter
        visible={!!showRolePermissions}
        onCancel={() => setShowRolePermissions(null)}
      >
        <Title level={4}>Access level</Title>
        <AccessControlMatrix role={showRolePermissions} />
      </StyledModal>
    </>
  );
};

const StyledSearchRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 2rem;
  width: 100%;
`;

const StyledInput = styled(Input)`
  .cogs-input {
    border-radius: 6px;
  }
  .cogs-icon-Search {
    opacity: 0.25;
    left: 12px;
  }
  .input-clear-button {
    top: calc(50% - 28px / 2);
    right: 2px !important;
    width: 28px;
    height: 28px;
    .cogs-icon.input-content.input-icon.cogs-icon-Close {
      top: calc(50% - 12px / 2);
      width: 12px !important;
      height: 12px !important;
    }
  }
`;

const StyledOptionsMenu = styled(Flex)`
  .cogs-icon,
  .cogs-body-1 {
    color: var(--cogs-danger);
  }
`;

const StyledModal = styled(Modal)`
  display: flex;
  flex: 1;
  justify-content: center;
  min-width: 50vw;
  border-radius: 12px;
  .cogs-modal-close {
    cursor: pointer;
  }
  .cogs-title-4 {
    margin: 0 0 1rem 1rem;
  }
`;
